import React, { useEffect } from 'react';
import { XMarkIcon, TruckIcon } from '@heroicons/react/24/outline';

/**
 * NotificationToast Component
 * Displays load match notifications as a toast popup in the bottom-right corner
 *
 * Props:
 * - notification: Object containing load match details
 * - onClose: Function to call when toast is dismissed
 * - autoClose: Boolean to enable auto-close (default: true)
 * - duration: Duration in ms before auto-close (default: 5000)
 */
const NotificationToast = ({ notification, onClose, autoClose = true, duration = 5000 }) => {
  useEffect(() => {
    if (autoClose && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  if (!notification) return null;

  // Extract notification details
  const { loadDetails, filterName } = notification;
  const origin = loadDetails?.origin?.city || 'Unknown';
  const originState = loadDetails?.origin?.state || '';
  const destination = loadDetails?.destination?.city || 'Unknown';
  const destinationState = loadDetails?.destination?.state || '';
  const loadId = loadDetails?.loadId || 'N/A';

  return (
    <div
      className="w-80 max-w-[calc(100vw-2rem)]"
      role="alert"
      aria-live="polite"
      style={{
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes shrinkWidth {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}
      </style>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-blue-500 dark:bg-blue-600 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-white">
            <TruckIcon className="h-4 w-4" />
            <span className="font-semibold text-xs">New Load Match!</span>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-600 dark:hover:bg-blue-700
                     rounded p-0.5 transition-colors"
            aria-label="Close notification"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          {/* Filter Name */}
          {filterName && (
            <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
              <span className="font-medium text-gray-700 dark:text-gray-300">{filterName}</span>
            </div>
          )}

          {/* Route */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                {origin}{originState ? `, ${originState}` : ''}
              </div>
            </div>
            <div className="flex-shrink-0">
              <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
            <div className="flex-1 min-w-0 text-right">
              <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                {destination}{destinationState ? `, ${destinationState}` : ''}
              </div>
            </div>
          </div>

          {/* Load Details */}
          <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400">
            <span className="font-mono text-gray-800 dark:text-gray-200 truncate">{loadId}</span>
            {loadDetails?.rate && (
              <span className="font-semibold text-green-600 dark:text-green-400 ml-2">
                ${loadDetails.rate}
              </span>
            )}
          </div>

          {/* Additional Info */}
          {(loadDetails?.pickupDate || loadDetails?.equipment) && (
            <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
              {loadDetails?.pickupDate && (
                <span className="truncate">{new Date(loadDetails.pickupDate).toLocaleDateString()}</span>
              )}
              {loadDetails?.equipment && (
                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 text-[9px] truncate">
                  {loadDetails.equipment}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Progress Bar (auto-close indicator) */}
        {autoClose && duration > 0 && (
          <div className="h-0.5 bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full bg-blue-500 dark:bg-blue-600 transition-all ease-linear"
              style={{
                animation: `shrinkWidth ${duration}ms linear forwards`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * NotificationToastContainer Component
 * Manages multiple toast notifications with stacking from bottom to top
 */
export const NotificationToastContainer = ({ notifications, onDismiss }) => {
  const getSettings = () => {
    try {
      const settings = localStorage.getItem('loadboard:notificationSettings');
      return settings ? JSON.parse(settings) : {};
    } catch {
      return {};
    }
  };

  const settings = getSettings();
  const popupEnabled = settings.popupEnabled ?? false;
  const duration = settings.popupDuration ?? 5000;

  if (!popupEnabled || !notifications || notifications.length === 0) {
    return null;
  }

  // Reverse notifications so newest appears at the bottom
  const reversedNotifications = [...notifications].reverse();

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-2 pointer-events-none">
      {reversedNotifications.map((notification, index) => (
        <div
          key={notification.id || index}
          className="pointer-events-auto transition-all duration-300 ease-out"
          style={{
            zIndex: 50 + index // Newest (at bottom) has highest z-index
          }}
        >
          <NotificationToast
            notification={notification}
            onClose={() => onDismiss(notification.id || index)}
            autoClose={true}
            duration={duration}
          />
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
